import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Shield, 
  Car, 
  FileText, 
  QrCode, 
  Download, 
  Share2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Wrench,
  Calendar,
  DollarSign,
  Lock,
  AlertTriangle,
  Copy,
  ExternalLink,
  Fingerprint,
  Hash,
  MapPin,
  User,
  Star,
  ArrowRightLeft,
  Printer
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const CarPassportPage = () => {
  const { API, sessionToken, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const printRef = useRef();

  const fetchVehicleData = useCallback(async () => {
    try {
      setLoading(true);
      const [vehicleRes, historyRes] = await Promise.all([
        axios.get(`${API}/vehicles/${vehicleId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }),
        axios.get(`${API}/vehicles/${vehicleId}/passport`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        })
      ]);
      
      setVehicle(vehicleRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      toast.error('Error al cargar datos del vehículo');
    } finally {
      setLoading(false);
    }
  }, [API, sessionToken, vehicleId]);

  useEffect(() => {
    fetchVehicleData();
  }, [fetchVehicleData]);

  const handleTransfer = async () => {
    if (!transferEmail) {
      toast.error('Ingrese el email del nuevo propietario');
      return;
    }
    
    try {
      await axios.post(`${API}/vehicles/${vehicleId}/transfer`, {
        new_owner_email: transferEmail
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      toast.success('Solicitud de transferencia enviada');
      setShowTransferDialog(false);
      setTransferEmail('');
    } catch (error) {
      toast.error('Error al procesar transferencia');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getBlockchainHash = () => {
    if (!vehicle) return '';
    // Simulated blockchain hash based on vehicle data
    const data = `${vehicle.id}-${vehicle.vin}-${vehicle.license_plate}`;
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `0x${Math.abs(hash).toString(16).padStart(64, '0').slice(0, 64)}`;
  };

  const shareUrl = vehicle ? `${window.location.origin}/car-passport/public/${vehicle.id}` : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 passport-dark-grid flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Verificando identidad del vehículo...</p>
          <p className="text-purple-300 text-sm">Conectando con blockchain...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-slate-950 passport-dark-grid flex items-center justify-center">
        <Card className="p-8 bg-slate-800/50 border-red-500/50 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Vehículo no encontrado</h2>
          <p className="text-slate-400 mb-4">No se pudo verificar la identidad del vehículo</p>
          <Button onClick={() => navigate('/client/vehicles')} variant="outline">
            Volver a mis vehículos
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 passport-dark-grid">
      {/* Security Header */}
      <header className="bg-black/40 backdrop-blur-xl border-b border-purple-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/client/vehicles')} 
                className="text-white hover:bg-white/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                    Car Passport
                  </h1>
                  <div className="flex items-center space-x-2">
                    <Lock className="w-3 h-3 text-green-400" />
                    <span className="text-xs text-green-400">Conexión Segura • Blockchain Verificado</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* QR Button */}
              <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20" data-testid="qr-btn">
                    <QrCode className="w-4 h-4 mr-2" />
                    QR
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-purple-500/50 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center space-x-2">
                      <QrCode className="w-5 h-5 text-purple-400" />
                      <span>Código QR del Car Passport</span>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center py-6">
                    <div className="bg-white p-4 rounded-xl">
                      <QRCode value={shareUrl} size={200} />
                    </div>
                    <p className="text-sm text-slate-400 mt-4 text-center">
                      Escanea este código para ver el historial verificado del vehículo
                    </p>
                    <div className="flex space-x-2 mt-4 w-full">
                      <Button 
                        onClick={() => copyToClipboard(shareUrl)} 
                        variant="outline" 
                        className="flex-1 border-purple-500/50"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar Link
                      </Button>
                      <Button 
                        onClick={() => window.open(shareUrl, '_blank')} 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Abrir
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Document Button */}
              <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20" data-testid="document-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Documento
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-purple-500/50 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-purple-400" />
                      <span>Documento Oficial Car Passport</span>
                    </DialogTitle>
                  </DialogHeader>
                  
                  {/* Printable Document */}
                  <div ref={printRef} className="bg-white text-black p-8 rounded-lg mt-4">
                    {/* Document Header */}
                    <div className="border-b-2 border-purple-600 pb-4 mb-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold text-purple-800">FLIX CAR PASSPORT</h2>
                          <p className="text-sm text-slate-600">Certificado Digital de Historial Vehicular</p>
                        </div>
                        <div className="text-right">
                          <div className="w-20 h-20 bg-slate-100 flex items-center justify-center">
                            <QRCode value={shareUrl} size={70} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <h3 className="font-bold text-purple-800 mb-2">INFORMACIÓN DEL VEHÍCULO</h3>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr><td className="text-slate-600 py-1">Marca/Modelo:</td><td className="font-medium">{vehicle.make} {vehicle.model}</td></tr>
                            <tr><td className="text-slate-600 py-1">Año:</td><td className="font-medium">{vehicle.year}</td></tr>
                            <tr><td className="text-slate-600 py-1">Color:</td><td className="font-medium">{vehicle.color}</td></tr>
                            <tr><td className="text-slate-600 py-1">Placa:</td><td className="font-medium">{vehicle.license_plate}</td></tr>
                            <tr><td className="text-slate-600 py-1">VIN:</td><td className="font-medium text-xs">{vehicle.vin || 'No registrado'}</td></tr>
                          </tbody>
                        </table>
                      </div>
                      <div>
                        <h3 className="font-bold text-purple-800 mb-2">DATOS FISCALES</h3>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr><td className="text-slate-600 py-1">Valor Fiscal:</td><td className="font-medium">{formatPrice(vehicle.fiscal_value || 5000000)}</td></tr>
                            <tr><td className="text-slate-600 py-1">Motor:</td><td className="font-medium">{vehicle.engine_number || vehicle.engine || 'N/A'}</td></tr>
                            <tr><td className="text-slate-600 py-1">Kilometraje:</td><td className="font-medium">{vehicle.mileage?.toLocaleString()} km</td></tr>
                            <tr><td className="text-slate-600 py-1">Marchamo:</td><td className={`font-medium ${vehicle.marchamo_paid ? 'text-green-600' : 'text-red-600'}`}>{vehicle.marchamo_paid ? 'AL DÍA' : 'PENDIENTE'}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Service History */}
                    <div className="mb-6">
                      <h3 className="font-bold text-purple-800 mb-2">HISTORIAL DE SERVICIOS ({history.length} registros)</h3>
                      <table className="w-full text-sm border">
                        <thead className="bg-purple-100">
                          <tr>
                            <th className="border p-2 text-left">Fecha</th>
                            <th className="border p-2 text-left">Servicio</th>
                            <th className="border p-2 text-left">Taller</th>
                            <th className="border p-2 text-right">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.slice(0, 10).map((entry, idx) => (
                            <tr key={entry.id || `history-${entry.date}-${entry.service_type}`} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                              <td className="border p-2">{formatShortDate(entry.date)}</td>
                              <td className="border p-2">{entry.service_type}</td>
                              <td className="border p-2">{entry.taller_name}</td>
                              <td className="border p-2 text-right">{formatPrice(entry.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer */}
                    <div className="border-t-2 border-purple-600 pt-4 text-center text-xs text-slate-500">
                      <p>Este documento es un certificado digital generado por FLIX.</p>
                      <p>Hash Blockchain: {getBlockchainHash().slice(0, 32)}...</p>
                      <p>Generado: {new Date().toLocaleString('es-CR')}</p>
                    </div>
                  </div>

                  <div className="flex space-x-2 mt-4">
                    <Button 
                      onClick={() => window.print()} 
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => copyToClipboard(shareUrl)}
                      className="flex-1 border-purple-500/50"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Compartir
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Transfer Button */}
              <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-700 active:scale-95 transition-transform" data-testid="transfer-btn">
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transferir
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-purple-500/50 text-white">
                  <DialogHeader>
                    <DialogTitle className="flex items-center space-x-2">
                      <ArrowRightLeft className="w-5 h-5 text-purple-400" />
                      <span>Transferir Vehículo</span>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 mb-6">
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-yellow-200 font-medium">Atención</p>
                          <p className="text-yellow-200/70 text-sm">
                            Al transferir el vehículo, el nuevo propietario recibirá acceso completo 
                            al Car Passport y todo su historial. Esta acción no se puede deshacer.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Email del Nuevo Propietario</Label>
                        <Input
                          type="email"
                          value={transferEmail}
                          onChange={(e) => setTransferEmail(e.target.value)}
                          placeholder="nuevo.propietario@email.com"
                          className="mt-2 bg-slate-800 border-purple-500/30 text-white"
                          data-testid="transfer-email-input"
                        />
                      </div>

                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-sm text-slate-400 mb-2">Vehículo a transferir:</p>
                        <p className="text-white font-medium">{vehicle.make} {vehicle.model} {vehicle.year}</p>
                        <p className="text-purple-300 text-sm">Placa: {vehicle.license_plate}</p>
                      </div>
                    </div>

                    <div className="flex space-x-2 mt-6">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowTransferDialog(false)}
                        className="flex-1 border-gray-600"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleTransfer}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        data-testid="confirm-transfer-btn"
                      >
                        Confirmar Transferencia
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Blockchain Verification Banner */}
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-green-300 font-medium">Identidad Verificada en Blockchain</p>
                <p className="text-green-400/60 text-xs font-mono">{getBlockchainHash().slice(0, 42)}...</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => copyToClipboard(getBlockchainHash())}
              className="text-green-400 hover:bg-green-500/10"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Main Vehicle Card */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          {/* Vehicle Identity */}
          <Card className="lg:col-span-2 bg-slate-800/50 border-purple-500/30 overflow-hidden">
            <div className="bg-purple-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Car className="w-12 h-12 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                      {vehicle.make} {vehicle.model}
                    </h2>
                    <p className="text-purple-200 text-lg">{vehicle.year} • {vehicle.color}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className="bg-white/20 text-white text-lg px-4 py-2 font-mono-data tracking-widest">
                    {vehicle.license_plate}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Technical Info */}
                <div>
                  <h3 className="text-purple-300 text-sm font-medium mb-4 flex items-center">
                    <Fingerprint className="w-4 h-4 mr-2" />
                    IDENTIFICACIÓN TÉCNICA
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Número de Motor</span>
                      <span className="text-white font-mono text-sm">{vehicle.engine_number || vehicle.engine || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">VIN / Chasis</span>
                      <span className="text-white font-mono text-xs">{vehicle.vin || 'No registrado'}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Transmisión</span>
                      <span className="text-white">{vehicle.transmission || 'Automático'}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Kilometraje Actual</span>
                      <span className="text-white font-medium">{vehicle.mileage?.toLocaleString() || 0} km</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-400">País de Registro</span>
                      <span className="text-white">{vehicle.country || 'Costa Rica'}</span>
                    </div>
                  </div>
                </div>

                {/* Fiscal Info */}
                <div>
                  <h3 className="text-purple-300 text-sm font-medium mb-4 flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    INFORMACIÓN FISCAL
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Valor Fiscal</span>
                      <span className="text-green-400 font-bold text-lg">{formatPrice(vehicle.fiscal_value || 5000000)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Derecho de Circulación</span>
                      {vehicle.marchamo_paid !== false ? (
                        <Badge className="bg-green-500/20 text-green-400 border border-green-500/50">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          AL DÍA
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 border border-red-500/50">
                          <XCircle className="w-3 h-3 mr-1" />
                          PENDIENTE
                        </Badge>
                      )}
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">Vence</span>
                      <span className="text-white">{vehicle.marchamo_expiry || '31 Dic 2025'}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-400">RTV</span>
                      {vehicle.rtv_status !== 'expired' ? (
                        <Badge className="bg-green-500/20 text-green-400 border border-green-500/50">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          VIGENTE
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          POR VENCER
                        </Badge>
                      )}
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-400">Servicios en Flix</span>
                      <span className="text-purple-400 font-bold">{history.length} registros</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-4">
            <Card className="bg-slate-800/50 border-purple-500/30 p-6">
              <h3 className="text-purple-300 text-sm font-medium mb-4 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                ESTADO DEL PASSPORT
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Verificación</span>
                  <Badge className="bg-green-500/20 text-green-400">Activo</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Historial</span>
                  <span className="text-white font-bold">{history.length} servicios</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Último servicio</span>
                  <span className="text-white text-sm">
                    {history.length > 0 ? formatShortDate(history[0]?.date) : 'Sin registros'}
                  </span>
                </div>
                <Separator className="bg-slate-700" />
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Total invertido</span>
                  <span className="text-green-400 font-bold">
                    {formatPrice(history.reduce((sum, h) => sum + (h.price || 0), 0))}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30 p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-8 h-8 text-purple-400" />
                </div>
                <h4 className="text-white font-medium mb-1">Valor Estimado de Reventa</h4>
                <p className="text-3xl font-bold text-purple-300">
                  {formatPrice((vehicle.fiscal_value || 5000000) * 1.15)}
                </p>
                <p className="text-xs text-purple-400 mt-2">
                  +15% por historial verificado
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Service History Timeline */}
        <Card className="bg-slate-800/50 border-purple-500/30">
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center" style={{ fontFamily: 'Outfit' }}>
                <Clock className="w-6 h-6 mr-3 text-purple-400" />
                Historial de Vida del Vehículo
              </h3>
              <Badge className="bg-purple-500/20 text-purple-300">
                {history.length} registros verificados
              </Badge>
            </div>
          </div>

          <div className="p-6">
            {history.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-10 h-10 text-slate-500" />
                </div>
                <h4 className="text-white font-medium mb-2">Sin registros aún</h4>
                <p className="text-slate-400 text-sm max-w-md mx-auto">
                  Los servicios realizados a través de Flix aparecerán aquí con todos sus detalles verificados en blockchain.
                </p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-purple-600 to-purple-800"></div>
                
                <div className="space-y-6">
                  {history.map((entry, index) => {
                    const date = new Date(entry.date);
                    const day = date.getDate();
                    const month = date.toLocaleDateString('es-CR', { month: 'short' });
                    const year = date.getFullYear();
                    
                    return (
                      <div key={entry.id || index} className="relative pl-16" data-testid={`history-entry-${index}`}>
                        {/* Timeline dot */}
                        <div className="absolute left-4 w-5 h-5 bg-purple-600 rounded-full border-4 border-slate-800 z-10"></div>
                        
                        <Card className="bg-slate-700/50 border-slate-600 hover:border-purple-500/50 transition-colors overflow-hidden">
                          <div className="flex">
                            {/* Date Column */}
                            <div className="bg-purple-600/20 p-4 flex flex-col items-center justify-center min-w-[100px] border-r border-slate-600">
                              <span className="text-3xl font-bold text-white">{day}</span>
                              <span className="text-purple-300 text-sm uppercase">{month}</span>
                              <span className="text-purple-400 text-xs">{year}</span>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <h4 className="text-white font-semibold text-lg">{entry.service_type}</h4>
                                  <p className="text-slate-400 text-sm">{entry.description}</p>
                                </div>
                                <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Verificado
                                </Badge>
                              </div>
                              
                              <div className="grid md:grid-cols-4 gap-4 text-sm">
                                <div className="flex items-center space-x-2">
                                  <Wrench className="w-4 h-4 text-purple-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Taller</p>
                                    <p className="text-white">{entry.taller_name}</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <DollarSign className="w-4 h-4 text-green-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Monto</p>
                                    <p className="text-green-400 font-medium">{formatPrice(entry.price)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <MapPin className="w-4 h-4 text-blue-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Kilometraje</p>
                                    <p className="text-white">{entry.mileage_at_service?.toLocaleString() || 'N/A'} km</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Hash className="w-4 h-4 text-purple-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Hash</p>
                                    <p className="text-purple-300 font-mono text-xs truncate max-w-[100px]">
                                      {entry.id?.slice(0, 8) || 'N/A'}...
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Blockchain Info Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            <Lock className="w-4 h-4 inline mr-1" />
            Todos los datos están protegidos y verificados mediante tecnología blockchain
          </p>
          <p className="text-slate-600 text-xs mt-2">
            Car Passport ID: {vehicle.id} • Última actualización: {formatDate(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarPassportPage;
